import {useMemo} from 'react'
import {MAIN_IMAGES_IMAGE_TYPE} from '../../constants'
import {findImageGroupBy} from '../../utils/image-groups-utils'

/**
 * Builds the full gallery items list for XLEAP product gallery.
 *
 * The hook:
 * - Selects the correct image group based on view type ("main") and selected variations
 * - Normalizes image assets (adds `type: 'image'` and resolves `src`)
 * - Prepends the main product video (`c_main-video`) if present
 * - Appends additional product videos and maps `videoId` → `brightcoveId`
 *
 * Returned items are ordered as:
 * 1. Main product video (if exists)
 * 2. Images
 * 3. Videos
 *
 *  Differences compared to `useCockpitGalleryImages`:
 *
 * - Uses images from the image group with the new **MAIN view type**
 * - Does NOT use c_extraMainImage or c_extraMainImage2 images.
 * - Does NOT sort or reorder videos.
 * - Returns c_main-video (type: 'direct') as first and images and videos in their original order as provided by the backend.
 *
 *
 * @param {Object} product
 * @param {Object} product.imageGroups - Image groups from SFCC product
 * @param {Object} [product['c_main-video']] - Main product video asset
 *
 * @param {Object} derivedProduct
 * @param {Array<Object>} [derivedProduct.videos] - brightcove product videos
 * @param {Object} derivedProduct.variationParams - Selected variation attributes
 *
 * @returns {Array<Object>} Normalized gallery items ready for rendering
 */

const useXleapGalleryImages = (product, derivedProduct) => {
    const {videos, variationParams} = derivedProduct || {}

    const imageGroups = product?.imageGroups
    const viewType = MAIN_IMAGES_IMAGE_TYPE

    const images = useMemo(() => {
        return (
            findImageGroupBy(imageGroups, {
                viewType,
                selectedVariationAttributes: variationParams
            })?.images || []
        )
    }, [imageGroups, viewType, variationParams])

    const galleryItems = useMemo(() => {
        return [
            product?.['c_main-video'] && {
                ...product['c_main-video'],
                type: 'direct',
                videoControls: false,
                showVideoInfo: true
            },

            ...images.map((img) => ({
                ...img,
                type: 'image',
                src: img.disBaseLink || img.link
            })),

            ...(videos
                ? videos.map((video) => ({
                      ...video,
                      videoControls: false,
                      showVideoInfo: true,
                      brightcoveId: video.videoId,
                      poster: {
                          src: video.poster
                      }
                  }))
                : [])
        ].filter(Boolean)
    }, [product, images, videos])

    return galleryItems
}

export default useXleapGalleryImages
